import {
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
} from "react";
import { FieldError } from "react-hook-form";
import { StyledPhoneInput, FormControl, FormLabel, FormErrorMessage } from "./styles";
import { PhoneInputRefType, ParsedCountry } from "react-international-phone";

interface PhoneInputChange {
  phone: string;
  dialCode: string;
  inputValue: string;
}

interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  id: string;
  label?: string;
  error?: string | FieldError;
  showErrorMessage?: boolean;
  value: string;
  onChange?: (value: PhoneInputChange) => void;
  labelTextColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  focusBorderColor: string;
  inputPlaceholderColor?: string;
  inputTextColor?: string;
}

const PhoneInputBase: ForwardRefRenderFunction<PhoneInputRefType, PhoneInputProps> = (
  { id,
    label,
    error,
    showErrorMessage = true,
    labelTextColor,
    backgroundColor,
    borderColor,
    focusBorderColor,
    inputPlaceholderColor,
    inputTextColor,
    value,
    ...rest
  },
  ref
) => {
  return (
    <FormControl isInvalid={!!error} >
      {!!label && <FormLabel htmlFor={id} $textColor={labelTextColor}>{label}</FormLabel>}
      <StyledPhoneInput
        ref={ref}
        defaultCountry="br"
        disableCountryGuess={true}
        disableDialCodeAndPrefix={true}
        showDisabledDialCodeAndPrefix={false}
        placeholder={rest.placeholder}
        aria-invalid={!!error && showErrorMessage ? "true" : "false"}
        value={String(value)}
        onChange={(phone: string, meta: { country: ParsedCountry; inputValue: string }) => {
          if (typeof rest.onChange === "function") {
            rest.onChange({
              phone,
              dialCode: meta.country.dialCode,
              inputValue: meta.inputValue,
            });
          }
        }}
        inputProps={{
          id,
          name: rest.name || 'phone',
          required: rest.required,
          autoFocus: rest.autoFocus,
          autoComplete: rest.autoComplete || "tel",
        }}
        $backgroundColor={backgroundColor}
        $borderColor={borderColor}
        $focusBorderColor={focusBorderColor}
        $placeholderColor={inputPlaceholderColor}
        $textColor={inputTextColor}
      />
      {!!error && showErrorMessage && (
        <FormErrorMessage data-testid="error-message">
          {typeof error === 'string' ? error : error?.message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export const PhoneInput = forwardRef(PhoneInputBase);
